import { useMemo } from "react";

import { Grid } from "@mui/material";

import DisplayPropertiesCard from "@covertree/ui/content/DisplayPropertiesCard";

import { ViewEnteredAddressOnMap } from "@covertree/features";
import { GeoCodeResult, PolicyUsage } from "@covertree/new-business-graphql";
import { LoadingOrValue } from "@covertree/ui";

import { mapAddressToAddressLines } from "@utils/mapAddressToAddressLines";
import { mapHomeType } from "@utils/mapHomeType";
import { mapRoofShape } from "@utils/mapRoofShape";
import { mapSkirtingType } from "@utils/mapSkirtingType";

import { mapUnitLocationLabel } from "../utils/mapUnitLocationLabel";
import { PolicyDetailsContentProps } from "../../../types";

export function UnitDetails({ policy, loading }: PolicyDetailsContentProps) {
  const [propertyAddressLine1, propertyAddressLine2] = useMemo(
    () => mapAddressToAddressLines(policy?.units[0]?.address),
    [policy?.units[0]?.address]
  );

  const geoCodeData: GeoCodeResult = useMemo(
    () => ({
      state: policy?.units[0]?.address?.state,
      homeLocation: policy?.units[0]?.details?.unitLocation,
      communityName: policy?.units[0]?.details?.communityName,
      county: policy?.units[0]?.address?.county,
      formattedLocationAddress:
        policy?.units[0]?.details?.formattedLocationAddress,
      latitude: +policy?.units[0]?.details?.latitude,
      longitude: +policy?.units[0]?.details?.longitude,
    }),
    [policy]
  );

  return (
    <Grid item xs={12}>
      <DisplayPropertiesCard
        properties={[
          {
            label: "Property address",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={propertyAddressLine1}
                height={24}
                width={121}
              />
            ),
            valueLine2: propertyAddressLine2,
            showAsTypography: true,
            gridSize: 12,
            inlineButton: policy?.units[0]?.address ? (
              <ViewEnteredAddressOnMap
                isValid
                isNewPolicyFlow
                result={geoCodeData}
              />
            ) : undefined,
          },
          {
            label: "Unit ID",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={policy?.units[0]?.unitId}
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          {
            label: "Occupancy type",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={
                  policy?.mainUnitType
                    ? policy.mainUnitType === PolicyUsage.Rental
                      ? "Landlord"
                      : policy.mainUnitType
                    : undefined
                }
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          {
            label: "Make",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={policy?.units[0]?.construction?.manufacturerName}
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          ...(policy?.units[0]?.construction?.makeCustom
            ? [
                {
                  label: "Custom make",
                  value: (
                    <LoadingOrValue
                      loading={!policy && loading}
                      value={policy?.units[0]?.construction?.makeCustom}
                      height={24}
                      width={121}
                    />
                  ),
                  showAsTypography: true,
                },
              ]
            : []),
          {
            label: "Model",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={policy?.units[0]?.construction?.modelHome}
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          {
            label: "Model year",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={policy?.units[0]?.construction?.modelYear?.toString()}
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          {
            label: "Home type",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={mapHomeType(policy?.units[0]?.construction?.homeType)}
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          {
            label: "Home length",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={policy?.units[0]?.construction?.homeLength}
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          {
            label: "Home width",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={policy?.units[0]?.construction?.homeWidth}
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          {
            label: "Square footage",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={policy?.units[0]?.construction?.totalSquareFootage}
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          {
            label: "Roof condition",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={policy?.units[0]?.construction?.roofCondition}
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          {
            label: "Roof year",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={policy?.units[0]?.construction?.roofYearYyyy?.toString()}
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          {
            label: "Roof type",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={mapRoofShape(policy?.units[0]?.construction?.roofShape)}
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          {
            label: "HUD Code",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={policy?.units[0]?.construction?.homeHudNumber}
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          {
            label: "Skirting type",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={mapSkirtingType(
                  policy?.units[0]?.construction?.skirtingType
                )}
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          {
            label: "Community type",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={
                  mapUnitLocationLabel(
                    policy?.units[0]?.details?.unitLocation
                  ) ?? "N/A"
                }
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
          {
            label: "Community name",
            value: (
              <LoadingOrValue
                loading={!policy && loading}
                value={policy?.units[0]?.details?.communityName ?? "N/A"}
                height={24}
                width={121}
              />
            ),
            showAsTypography: true,
          },
        ]}
      />
    </Grid>
  );
}
